import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Text,
  Textarea,
  Wrap,
  WrapItem,
  useColorModeValue,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useEffect, useRef, useState } from "react";
import { AsyncSelect } from "components/select/SelectConTema";
import actividadesServiceGlobal from "services/actividadesService/actividades.service";
import { hoyLocal } from "utils/fechasLocales";
import { COLOR_TIPO, ICONO_TIPO } from "./actividades";

const selectEnPortal = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const opcionCliente = (cliente) => (cliente?.id ? { value: cliente.id, label: cliente.nombre } : null);

const formularioDesde = (actividad, iniciales = {}) => {
  if (actividad) {
    return {
      id: actividad.id,
      tipo: actividad.tipo,
      asunto: actividad.asunto,
      fecha: actividad.fecha || hoyLocal(),
      hora: actividad.hora || "",
      completada: actividad.completada,
      resultado: actividad.resultado || "",
      descripcion: actividad.descripcion || "",
      cliente: opcionCliente(actividad.cliente),
      contactoId: actividad.contacto?.id ? String(actividad.contacto.id) : "",
      oportunidadId: actividad.oportunidad?.id ? String(actividad.oportunidad.id) : "",
      asesorId: actividad.responsable?.id ? String(actividad.responsable.id) : "",
    };
  }
  return {
    id: null,
    tipo: iniciales.tipo || "LLAMADA",
    asunto: iniciales.asunto || "",
    fecha: iniciales.fecha || hoyLocal(),
    hora: "",
    completada: Boolean(iniciales.completada),
    resultado: "",
    descripcion: "",
    cliente: opcionCliente(iniciales.cliente),
    contactoId: iniciales.contacto?.id ? String(iniciales.contacto.id) : "",
    oportunidadId: iniciales.oportunidad?.id ? String(iniciales.oportunidad.id) : "",
    asesorId: "",
  };
};

export default function FormularioActividad({
  isOpen,
  onClose,
  actividad = null,
  valoresIniciales = {},
  bloquearVinculos = false,
  catalogos,
  onGuardada,
  onEliminar,
}) {
  const textoSecundario = useColorModeValue("gray.600", "gray.300");
  const fondoVinculo = useColorModeValue("gray.50", "whiteAlpha.100");

  const [formulario, setFormulario] = useState(() => formularioDesde(actividad, valoresIniciales));
  const [datosCliente, setDatosCliente] = useState({ contactos: [], oportunidades: [] });
  const [cargandoCliente, setCargandoCliente] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  // Evita que la respuesta de un cliente anterior pise la del que se eligió
  // después si llegan en desorden.
  const clientePedido = useRef(null);

  const editando = Boolean(formulario.id);
  const esNota = formulario.tipo === "NOTA";
  const hecha = esNota || formulario.completada;

  const cambiar = (campo, valor) => setFormulario((previo) => ({ ...previo, [campo]: valor }));

  const cargarDatosCliente = async (clienteId) => {
    clientePedido.current = clienteId;
    if (!clienteId) {
      setCargandoCliente(false);
      setDatosCliente({ contactos: [], oportunidades: [] });
      return;
    }
    setCargandoCliente(true);
    const respuesta = await actividadesServiceGlobal.getDatosCliente({ clienteId });
    if (clientePedido.current !== clienteId) return;
    setCargandoCliente(false);
    setDatosCliente({
      contactos: Array.isArray(respuesta?.contactos) ? respuesta.contactos : [],
      oportunidades: Array.isArray(respuesta?.oportunidades) ? respuesta.oportunidades : [],
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const inicial = formularioDesde(actividad, valoresIniciales);
    setFormulario(inicial);
    setError("");
    cargarDatosCliente(inicial.cliente?.value || null);
  // Se reinicia al abrir; los valores iniciales llegan como objeto nuevo en
  // cada render del padre y no deben reiniciarlo mientras se escribe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, actividad]);

  const buscarClientes = async (textoBuscado) => {
    const respuesta = await actividadesServiceGlobal.buscarClientes({ search: textoBuscado });
    const clientes = Array.isArray(respuesta?.data) ? respuesta.data : [];
    return clientes.map((cliente) => ({
      value: cliente.id,
      label: cliente.nit ? `${cliente.nombre} · ${cliente.nit}` : cliente.nombre,
    }));
  };

  const cambiarCliente = (opcion) => {
    // El contacto y el negocio eran del cliente anterior.
    setFormulario((previo) => ({ ...previo, cliente: opcion, contactoId: "", oportunidadId: "" }));
    cargarDatosCliente(opcion?.value || null);
  };

  const guardar = async () => {
    if (guardando) return;
    if (!formulario.asunto.trim()) {
      setError("Escribe el asunto de la actividad.");
      return;
    }
    if (!formulario.fecha) {
      setError("Indica la fecha.");
      return;
    }

    setError("");
    setGuardando(true);
    const respuesta = await actividadesServiceGlobal.saveActividad({
      id: formulario.id,
      tipo: formulario.tipo,
      asunto: formulario.asunto.trim(),
      fecha: formulario.fecha,
      hora: formulario.hora || null,
      completada: hecha,
      resultado: hecha ? formulario.resultado : null,
      descripcion: formulario.descripcion,
      clienteId: formulario.cliente?.value || null,
      contactoId: formulario.contactoId || null,
      oportunidadId: formulario.oportunidadId || null,
      asesorId: catalogos.puedeVerTodo ? formulario.asesorId || null : null,
    });
    setGuardando(false);

    if (respuesta?.error || !respuesta?.data) {
      setError(respuesta?.error || "No se pudo guardar la actividad");
      return;
    }
    onGuardada(respuesta.data, respuesta.message);
  };

  // El negocio fijo (al registrar desde la oportunidad) puede no estar en la
  // lista de abiertos del cliente si ya se cerró: se agrega para no perderlo.
  const opcionesOportunidad = [...datosCliente.oportunidades];
  const oportunidadFija = actividad?.oportunidad || valoresIniciales.oportunidad;
  if (oportunidadFija?.id && !opcionesOportunidad.some((item) => item.id === oportunidadFija.id)) {
    opcionesOportunidad.push(oportunidadFija);
  }

  const opcionesAsesor = [...catalogos.asesores];
  if (actividad?.responsable?.id && !opcionesAsesor.some((item) => item.id === actividad.responsable.id)) {
    opcionesAsesor.push(actividad.responsable);
  }

  const titulo = editando ? "Editar actividad" : hecha ? "Registrar actividad" : "Programar actividad";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader>{titulo}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {error && (
            <Alert status="error" mb="14px" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl mb="14px">
            <FormLabel fontSize="sm">Tipo</FormLabel>
            <Wrap spacing="8px">
              {catalogos.tipos.map((tipo) => {
                const activo = formulario.tipo === tipo.clave;
                return (
                  <WrapItem key={tipo.clave}>
                    <Button
                      size="sm"
                      leftIcon={<Icon as={ICONO_TIPO[tipo.clave] || ICONO_TIPO.TAREA} />}
                      colorScheme={activo ? COLOR_TIPO[tipo.clave] || "gray" : "gray"}
                      variant={activo ? "solid" : "outline"}
                      onClick={() => cambiar("tipo", tipo.clave)}
                      aria-pressed={activo}
                    >
                      {tipo.nombre}
                    </Button>
                  </WrapItem>
                );
              })}
            </Wrap>
          </FormControl>

          <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }} gap="14px">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Asunto</FormLabel>
              <Input
                value={formulario.asunto}
                onChange={(evento) => cambiar("asunto", evento.target.value)}
                placeholder="Ej. Llamar para confirmar la orden"
                maxLength={200}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Fecha</FormLabel>
              <Input type="date" value={formulario.fecha} onChange={(evento) => cambiar("fecha", evento.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Hora</FormLabel>
              <Input type="time" value={formulario.hora} onChange={(evento) => cambiar("hora", evento.target.value)} />
            </FormControl>
          </Grid>

          {!esNota && (
            <FormControl display="flex" alignItems="center" mt="14px">
              <Switch
                id="actividad-hecha"
                isChecked={formulario.completada}
                onChange={(evento) => cambiar("completada", evento.target.checked)}
                colorScheme="green"
                mr="10px"
              />
              <FormLabel htmlFor="actividad-hecha" mb="0" fontSize="sm">
                Ya se realizó
              </FormLabel>
            </FormControl>
          )}

          {hecha && (
            <FormControl mt="14px">
              <FormLabel fontSize="sm">{esNota ? "Nota" : "Resultado"}</FormLabel>
              <Textarea
                value={formulario.resultado}
                onChange={(evento) => cambiar("resultado", evento.target.value)}
                placeholder={esNota ? "Lo que hay que recordar" : "Qué se habló, qué se acordó"}
                maxLength={4000}
                rows={3}
              />
            </FormControl>
          )}

          <Text fontWeight="700" fontSize="sm" mt="18px" mb="8px">Relacionada con</Text>
          {bloquearVinculos ? (
            <Box bg={fondoVinculo} borderRadius="10px" p="10px" mb="14px">
              <Text fontSize="sm">{formulario.cliente?.label || "Sin cliente"}</Text>
              {oportunidadFija?.nombre && (
                <Text fontSize="xs" color={textoSecundario}>Negocio: {oportunidadFija.nombre}</Text>
              )}
            </Box>
          ) : (
            <FormControl mb="14px">
              <FormLabel fontSize="sm">Cliente</FormLabel>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={buscarClientes}
                value={formulario.cliente}
                onChange={cambiarCliente}
                placeholder="Busca por nombre o NIT (opcional)"
                noOptionsMessage={() => "No hay clientes activos con ese nombre"}
                loadingMessage={() => "Buscando..."}
                menuPortalTarget={document.body}
                styles={selectEnPortal}
                isClearable
              />
            </FormControl>
          )}

          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="14px">
            <FormControl>
              <FormLabel fontSize="sm">Contacto</FormLabel>
              <Select
                value={formulario.contactoId}
                onChange={(evento) => cambiar("contactoId", evento.target.value)}
                isDisabled={!formulario.cliente || cargandoCliente}
                placeholder={cargandoCliente ? "Cargando..." : "Sin contacto"}
              >
                {datosCliente.contactos.map((contacto) => (
                  <option key={contacto.id} value={String(contacto.id)}>{contacto.nombre}</option>
                ))}
              </Select>
            </FormControl>
            {!bloquearVinculos && (
              <FormControl>
                <FormLabel fontSize="sm">Negocio</FormLabel>
                <Select
                  value={formulario.oportunidadId}
                  onChange={(evento) => cambiar("oportunidadId", evento.target.value)}
                  isDisabled={!formulario.cliente || cargandoCliente}
                  placeholder={cargandoCliente ? "Cargando..." : "Ninguno"}
                >
                  {opcionesOportunidad.map((oportunidad) => (
                    <option key={oportunidad.id} value={String(oportunidad.id)}>{oportunidad.nombre}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            {catalogos.puedeVerTodo && (
              <FormControl>
                <FormLabel fontSize="sm">Responsable</FormLabel>
                <Select
                  value={formulario.asesorId}
                  onChange={(evento) => cambiar("asesorId", evento.target.value)}
                  placeholder={editando ? undefined : "Yo"}
                >
                  {opcionesAsesor.map((asesor) => (
                    <option key={asesor.id} value={String(asesor.id)}>{asesor.nombre}</option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          {!esNota && (
            <FormControl mt="14px">
              <FormLabel fontSize="sm">Descripción</FormLabel>
              <Textarea
                value={formulario.descripcion}
                onChange={(evento) => cambiar("descripcion", evento.target.value)}
                placeholder="Detalles para preparar la actividad"
                maxLength={4000}
                rows={2}
              />
            </FormControl>
          )}
        </ModalBody>

        <ModalFooter gap="10px" flexWrap="wrap">
          {editando && onEliminar && (
            <Button
              leftIcon={<DeleteIcon />}
              colorScheme="red"
              variant="ghost"
              mr="auto"
              onClick={() => onEliminar(actividad)}
              isDisabled={guardando}
            >
              Eliminar
            </Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="brand" onClick={guardar} isLoading={guardando} loadingText="Guardando">
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
